import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  DotCircleIcon,
  HollowCircleIcon,
  TargetIcon,
  TrendUpIcon,
} from '@utils/icons';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { fetchSchedules, fetchSchedule } from '@apis/schedule';
import { scheduleListKey, scheduleKey } from '@constants/queryKeys';
import { useActiveScheduleNumber } from '@hooks/useActiveSchedule';
import type { ISchedulerData } from '@interfaces/IScheduler';
import type { IProjectProgressHeadline } from '@interfaces/IProjectProgress';
import { derivePhases, deriveHeadline } from './projectProgress.derive';
import { projectTodayMs, scheduleRetry } from './_shared/scheduleQueries';
import { DashboardCard } from './_shared/DashboardCard';
import { ProjectProgressShimmer } from '../DashboardSkeleton';
import styles from './projectProgressCard.module.css';

/* Project Progress — half-width card. Reads the project's Scheduler (工程表):
   the most-recent schedule's main activities (Gantt groups) and their
   sub-activities. Overall completion + per-activity bars come from the
   Scheduler's progress; planned / variance / SPI are derived from each
   sub-activity's planned start/end dates. Data is the live `/api/schedule`
   endpoint (shared query cache with the Scheduler page). */

const RING_RADIUS = 18;
const RING_CIRCUM = 2 * Math.PI * RING_RADIUS;

export function ProjectProgressCard() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();

  // Two-step: list the site's schedules, then load the most-recent one. Same
  // query keys as the Scheduler page, so the cache is shared.
  const schedulesQuery = useQuery({
    queryKey: scheduleListKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchSchedules(siteId!, signal),
    enabled: Boolean(siteId),
    staleTime: 5 * 60_000,
    retry: scheduleRetry,
  });
  // The schedule the user picked in the TopBar (shared across every surface);
  // falls back to the most-recent schedule when nothing is selected.
  const scheduleNumber = useActiveScheduleNumber(siteId, schedulesQuery.data ?? []);
  const scheduleQuery = useQuery({
    queryKey: scheduleKey(siteId ?? '', scheduleNumber),
    queryFn: ({ signal }) => fetchSchedule(siteId!, scheduleNumber!, signal),
    enabled: Boolean(siteId) && Boolean(scheduleNumber),
    staleTime: Infinity,
    retry: scheduleRetry,
  });

  /* The project's own zone decides which day is "today" — a site east of the
     viewer has already rolled over. No default zone: with none resolved yet the
     derivation is skipped rather than dated against a guess. */
  const tz = useSelector(selectSiteTz);
  const data = scheduleQuery.data;
  const isLoading = schedulesQuery.isLoading || scheduleQuery.isLoading;
  const isError = schedulesQuery.isError || scheduleQuery.isError;
  const headline = data && tz ? deriveHeadline(data, projectTodayMs(tz)) : null;

  return (
    <DashboardCard
      title={t('dashboard.projectProgress.title')}
      titleIcon={<span className={styles.titleIconTeal}><TargetIcon /></span>}
      headerRight={
        headline ? (
          <span className={styles.spi}>
            {t('dashboard.projectProgress.spi', {
              value: headline.spi === null ? '—' : headline.spi.toFixed(2),
            })}
          </span>
        ) : undefined
      }
      bodyClassName={styles.body}
    >
      {isLoading ? (
        <ProjectProgressShimmer />
      ) : isError ? (
        <div className={styles.stateMsg}>{t('dashboard.projectProgress.loadFailed')}</div>
      ) : !data || !headline ? (
        <div className={styles.stateMsg}>{t('dashboard.projectProgress.noSchedule')}</div>
      ) : (
        <ProjectProgressBody data={data} headline={headline} />
      )}
    </DashboardCard>
  );
}

/** The populated card body — ring + headline + phase bars. Rendered only when a
 *  schedule has loaded, so `data`/`headline` are always present here. */
function ProjectProgressBody({ data, headline }: { data: ISchedulerData; headline: IProjectProgressHeadline }) {
  const { t } = useTranslation();
  const phases = derivePhases(data);
  const dash = (headline.overallPct / 100) * RING_CIRCUM;

  return (
    <>
      <div className={styles.headlineRow}>
        <div className={styles.ringWrap}>
          <svg className={styles.ring} viewBox="0 0 48 48" aria-hidden>
            <circle cx="24" cy="24" r={RING_RADIUS} fill="none" stroke="var(--g100)" strokeWidth="5" />
            <circle
              cx="24" cy="24" r={RING_RADIUS}
              fill="none" stroke="var(--dashboard-teal)" strokeWidth="5" strokeLinecap="round"
              strokeDasharray={`${dash} ${RING_CIRCUM - dash}`}
              transform="rotate(-90 24 24)"
            />
          </svg>
          <span className={styles.ringValue}>{headline.overallPct}%</span>
        </div>
        <div className={styles.headlineText}>
          <span className={styles.headlineTitle}>{t('dashboard.projectProgress.overall')}</span>
          <span className={styles.headlineMeta}>
            {t('dashboard.projectProgress.plannedVariance', {
              planned: headline.plannedPct,
              variance: headline.variancePct,
            })}
          </span>
          {headline.variancePct !== 0 && (
            <span className={styles.headlineNote}>
              <TrendUpIcon size={12} />
              {headline.variancePct < 0
                ? t('dashboard.projectProgress.behindPlan', { value: Math.abs(headline.variancePct) })
                : t('dashboard.projectProgress.aheadPlan', { value: headline.variancePct })}
            </span>
          )}
        </div>
      </div>

      <ul className={styles.phases}>
        {phases.map((p) => {
          const iconCls =
            p.status === 'complete'    ? styles.iconComplete    :
            p.status === 'in-progress' ? styles.iconInProgress  :
            styles.iconPending;
          const Icon =
            p.status === 'complete'    ? <CheckCircleIcon size={16} /> :
            p.status === 'in-progress' && p.pct > 30 ? <HollowCircleIcon /> :
            p.status === 'in-progress' ? <DotCircleIcon /> :
            <HollowCircleIcon />;
          const label = p.key === 'other' ? t('dashboard.projectProgress.other') : p.name;
          return (
            <li key={p.key} className={styles.phase}>
              <span className={`${styles.phaseIcon} ${iconCls}`}>{Icon}</span>
              <span className={styles.phaseName}>{label}</span>
              <div className={styles.phaseBar}>
                <div className={styles.phaseFill} style={{ width: `${p.pct}%` }} />
              </div>
              <span className={styles.phasePct}>{p.pct}%</span>
            </li>
          );
        })}
      </ul>
    </>
  );
}
